import React from "react";
import { Link } from "react-router-dom";
import { Empty, Tag, Timeline, Typography } from "antd";
import { useTranslation } from "react-i18next";
import {
  formatCents,
  formatDate
} from "../../../serviceProvider/components/accounting/shared/money";
import {
  correctionReasonLabels,
  creditStatusMeta,
  invoiceStatusMeta,
  paymentMethodLabels
} from "../../../utils/invoiceStatusArray";

const KIND = {
  sent: { label: "Invoice sent", color: "blue" },
  payment: { label: "Payment received", color: "green" },
  offset: { label: "Offset with customer credit", color: "green" },
  payment_reversed: { label: "Payment reversed", color: "red" },
  credit_refund: { label: "Credit refunded", color: "gray" },
  credit_offset: { label: "Credit offset", color: "gray" },
  correction: { label: "Invoice correction", color: "orange", doc: true },
  cancellation: { label: "Cancellation invoice", color: "red", doc: true },
  correction_notice: { label: "Correction notice", color: "blue", doc: true },
  replacement: { label: "Replacement invoice", color: "blue", doc: true },
  replaced: { label: "Replaces invoice", color: "gray", doc: true },
  deducted_advance: { label: "Deducted advance invoice", color: "gray", doc: true },
  settled_by_final: { label: "Settled by final invoice", color: "green", doc: true },
  corrects: { label: "Corrected invoice", color: "gray", doc: true },
  final: { label: "Final invoice", color: "blue", doc: true },
  related: { label: "Related invoice", color: "gray", doc: true },
  dunning: { label: "Dunning letter", color: "orange" },
  write_off: { label: "Written off", color: "red" },
  write_off_revival: { label: "Payment after write-off", color: "green" },
  credit: { label: "Customer credit", color: "blue" }
};

/**
 * UI-2: the life of an invoice as a timeline — sent, payments, corrections, Storno, replacement,
 * dunning letters, write-off and customer credits. Each entry links to its document.
 * `events` come from GET /accounting/invoicing/:id/chain.
 */
const DocumentChain = ({ events, currentId }) => {
  const { t: ta } = useTranslation("accounting");
  if (!events?.length)
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={ta("No events yet")} />;

  const title = e => {
    const meta = KIND[e.kind] || { label: e.kind };
    const label = ta(meta.label);
    if (meta.doc && e.ref && String(e.ref) !== String(currentId))
      return (
        <Link to={`/invoices/details/${e.ref}`}>
          {label} {e.number || ""}
        </Link>
      );
    if (e.kind === "dunning" && e.ref)
      return (
        <Link to={`/user/accounting/receivables/dunning/${e.ref}`}>
          {label}
          {e.stage ? ` (${ta("Stage")} ${e.stage})` : ""}
        </Link>
      );
    if (e.kind === "credit") return <Link to="/user/accounting/receivables">{label}</Link>;
    if (
      ["payment", "offset", "payment_reversed", "credit_refund", "credit_offset"].includes(e.kind)
    )
      return <a href="#invoice-payments">{label}</a>;
    return (
      <span>
        {label} {e.number || ""}
      </span>
    );
  };

  const chips = e => {
    const out = [];
    if (e.draft) out.push(<Tag key="draft">{ta("Draft")}</Tag>);
    else if (KIND[e.kind]?.doc && e.status) {
      const m = invoiceStatusMeta(e.status);
      out.push(
        <Tag key="status" color={m.tag}>
          {ta(`status.${m.status}`)}
        </Tag>
      );
    }
    if (e.kind === "credit" && e.status) {
      const m = creditStatusMeta[e.status];
      if (m)
        out.push(
          <Tag key="credit" color={m.tag}>
            {ta(m.label)}
          </Tag>
        );
    }
    if (e.reason && correctionReasonLabels[e.reason])
      out.push(<Tag key="reason">{ta(correctionReasonLabels[e.reason])}</Tag>);
    if (e.method && paymentMethodLabels[e.method])
      out.push(<Tag key="method">{ta(paymentMethodLabels[e.method])}</Tag>);
    if (e.reversed)
      out.push(
        <Tag key="rev" color="error">
          {ta("Reversed")}
        </Tag>
      );
    return out;
  };

  const extra = e => {
    const parts = [];
    if (e.skonto) parts.push(`${ta("Skonto")} ${formatCents(e.skonto)}`);
    if (e.smallDifference)
      parts.push(`${ta("Small difference")} ${formatCents(e.smallDifference)}`);
    if (e.withheld) parts.push(`${ta("Construction withholding tax")} ${formatCents(e.withheld)}`);
    if (e.kind === "credit" && e.remaining !== undefined && e.remaining !== e.amount)
      parts.push(`${ta("Remaining")} ${formatCents(e.remaining)}`);
    if (e.note) parts.push(e.note);
    return parts.join(" · ");
  };

  return (
    <Timeline
      className="invoice-chain"
      items={events.map((e, i) => ({
        key: `${e.kind}-${e.ref || i}`,
        color: KIND[e.kind]?.color || "gray",
        children: (
          <div className="invoice-chain__item">
            <div className="invoice-chain__head">
              <span className="invoice-chain__title">{title(e)}</span>
              {e.amount ? (
                <strong className="invoice-chain__amount">{formatCents(e.amount)}</strong>
              ) : null}
            </div>
            <div className="invoice-chain__meta">
              <Typography.Text type="secondary">{formatDate(e.date)}</Typography.Text>
              {chips(e)}
            </div>
            {extra(e) ? (
              <Typography.Text type="secondary" className="invoice-chain__extra">
                {extra(e)}
              </Typography.Text>
            ) : null}
          </div>
        )
      }))}
    />
  );
};

export default DocumentChain;
